import React, { useEffect, useState } from 'react';
import {
    Box,
    Sheet,
    Typography,
    FormControl,
    FormLabel,
    Input,
    Button,
    Alert,
    CircularProgress,
    Divider
} from '@mui/joy';
import { useNavigate, useParams } from 'react-router-dom';
import Logo from '../components/Logo';
import axiosConfig from '../util/axiosConfig';
import { clearStoredAuth } from '../util/authStorage';
import { usePageTitle } from '../util/usePageTitle';

const RegisterPassword = () => {
    const navigate = useNavigate();
    const { hash } = useParams();
    usePageTitle('Set Password');

    const [validating, setValidating] = useState(true);
    const [studentId, setStudentId] = useState('');
    const [studentName, setStudentName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [linkInvalid, setLinkInvalid] = useState(false);
    const [showBrowserTip, setShowBrowserTip] = useState(false);

    useEffect(() => {
        clearStoredAuth();
        const constrainedView =
            window.matchMedia('(display-mode: standalone)').matches
            || window.matchMedia('(display-mode: minimal-ui)').matches
            || window.navigator.standalone === true;
        setShowBrowserTip(constrainedView);
    }, []);

    useEffect(() => {
        const validateLink = async () => {
            if (!hash) {
                setLinkInvalid(true);
                setError('Invalid registration link.');
                setValidating(false);
                return;
            }

            try {
                const response = await axiosConfig.get(
                    `/student-password-setup/validate/${encodeURIComponent(hash)}`
                );
                if (response.data?.success) {
                    setStudentId(response.data.studentid || '');
                    setStudentName(response.data.name || '');
                } else {
                    setLinkInvalid(true);
                    setError(response.data?.message || 'This link is invalid or has already been used.');
                }
            } catch (err) {
                setLinkInvalid(true);
                setError(err.response?.data?.message || 'This link is invalid or has already been used.');
            } finally {
                setValidating(false);
            }
        };

        validateLink();
    }, [hash]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axiosConfig.post('/student-password-setup/set-password', {
                studentid: studentId,
                hash,
                password
            });

            setSuccess(response.data?.message || 'Password set successfully! Redirecting to login...');
            clearStoredAuth();
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to set password. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'auto',
                minHeight: 'auto',
                alignItems: 'center',
                justifyContent: 'center',
                py: '20px',
                p: 2
            }}
        >
            <Sheet
                variant="outlined"
                sx={{
                    width: '100%',
                    maxWidth: 440,
                    mx: 'auto',
                    p: 3,
                    borderRadius: 'md',
                    boxShadow: 'md'
                }}
            >
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <Logo clickable={false} width={250} height={83} />
                </Box>

                <Typography level="h4" component="h1" sx={{ mb: 1, textAlign: 'center', color: '#404041' }}>
                    Set Your Password
                </Typography>
                <Typography level="body-sm" sx={{ mb: 2, textAlign: 'center', color: '#6e7072' }}>
                    Welcome to Vector Student Portal. Create your password to get started.
                </Typography>

                {showBrowserTip && (
                    <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                        For the best experience, copy this page URL from your mail app and open it in Chrome or Safari.
                    </Alert>
                )}

                {error && (
                    <Alert color="danger" variant="soft" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert color="success" variant="soft" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {validating ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                        <CircularProgress size="lg" />
                        <Typography level="body-md">Verifying your registration link...</Typography>
                    </Box>
                ) : linkInvalid ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography level="body-md" sx={{ color: '#606162', mb: 2 }}>
                            Please contact Vector India support if you need a new registration link.
                        </Typography>
                        <Button
                            fullWidth
                            onClick={() => navigate('/login')}
                            sx={{ bgcolor: '#ea7a23', '&:hover': { bgcolor: '#d06a1e' } }}
                        >
                            Go to Login
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Sheet variant="soft" color="neutral" sx={{ p: 2, mb: 2, borderRadius: 'md' }}>
                            <Typography level="body-sm" sx={{ color: '#6e7072', mb: 0.5 }}>
                                Student ID
                            </Typography>
                            <Typography level="title-md" sx={{ color: '#404041', mb: 1.5 }}>
                                {studentId}
                            </Typography>
                            {studentName && (
                                <>
                                    <Typography level="body-sm" sx={{ color: '#6e7072', mb: 0.5 }}>
                                        Name
                                    </Typography>
                                    <Typography level="title-md" sx={{ color: '#404041' }}>
                                        {studentName}
                                    </Typography>
                                </>
                            )}
                        </Sheet>

                        <Divider sx={{ my: 2 }} />

                        <form onSubmit={handleSubmit} autoComplete="off">
                            <FormControl required sx={{ mb: 2 }}>
                                <FormLabel>New Password</FormLabel>
                                <Input
                                    type="password"
                                    name="new-password"
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 6 characters"
                                    disabled={isSubmitting || Boolean(success)}
                                    sx={{
                                        '&:hover': { borderColor: '#ea7a23' },
                                        '&:focus-within': { borderColor: '#ea7a23' }
                                    }}
                                />
                            </FormControl>

                            <FormControl required sx={{ mb: 2 }}>
                                <FormLabel>Confirm Password</FormLabel>
                                <Input
                                    type="password"
                                    name="confirm-password"
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Re-enter your password"
                                    disabled={isSubmitting || Boolean(success)}
                                    sx={{
                                        '&:hover': { borderColor: '#ea7a23' },
                                        '&:focus-within': { borderColor: '#ea7a23' }
                                    }}
                                />
                            </FormControl>

                            <Button
                                type="submit"
                                fullWidth
                                loading={isSubmitting}
                                disabled={isSubmitting || Boolean(success)}
                                sx={{
                                    mt: 1,
                                    bgcolor: '#ea7a23',
                                    '&:hover': { bgcolor: '#d06a1e' }
                                }}
                            >
                                Set Password
                            </Button>
                        </form>
                    </>
                )}

                {!validating && !linkInvalid && (
                    <Button
                        variant="plain"
                        color="neutral"
                        fullWidth
                        type="button"
                        onClick={() => navigate('/login')}
                        sx={{ mt: 2 }}
                        disabled={isSubmitting}
                    >
                        Back to Login
                    </Button>
                )}
            </Sheet>
        </Box>
    );
};

export default RegisterPassword;
